import React, { useCallback, useEffect, useState } from "react";
import { commitMutation, graphql } from "react-relay";
import { Link } from "react-router-dom";

import { faBolt } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import * as Sentry from "@sentry/browser";
import { fetchQuery } from "relay-runtime";
import type { CheckboxProps } from "semantic-ui-react";

import MicrosoftTodoLogo from "~/integrations/microsoftTodo/MicrosoftTodoLogo";
import { type AuthPostMessage, type OAuthParams, promptOffice365Auth } from "~/lib/auth/index";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { environment } from "~/models";
import { Button } from "~/ui";
import { LoadingSpinner, toast } from "~/ui/";
import { __globalModalContext } from "~/ui/modals";

import Settings from "../../../account/Integrations/Settings";
import {
	connectUserToIntegration,
	disconnectUserFromIntegration,
	syncIncompleteActionItems,
	toggleUserIntegrationSync,
} from "../../ActionItemIntegrations";
import MicrosoftTodoAddListModal from "./MicrosoftTodoAddListModal";
import MicrosoftTodoDisconnectModal from "./MicrosoftTodoDisconnectModal";
import MicrosoftTodoListSettings from "./MicrosoftTodoListSettings";
import AboutMicrosoftTodo from "./MicrosoftTodoText";

import type { ActionItemIntegrationsConnectUserMutation } from "~/__generated__/ActionItemIntegrationsConnectUserMutation.graphql";
import type { ActionItemIntegrationsDisconnectUserMutation } from "~/__generated__/ActionItemIntegrationsDisconnectUserMutation.graphql";
import type { ActionItemIntegrationsSyncIncompleteActionItemsMutation } from "~/__generated__/ActionItemIntegrationsSyncIncompleteActionItemsMutation.graphql";
import type { ActionItemIntegrationsToggleSyncMutation } from "~/__generated__/ActionItemIntegrationsToggleSyncMutation.graphql";
import type { MicrosoftTodoIntegrationCreateUserMutation } from "~/__generated__/MicrosoftTodoIntegrationCreateUserMutation.graphql";
import type { MicrosoftTodoIntegrationListsQuery } from "~/__generated__/MicrosoftTodoIntegrationListsQuery.graphql";
import type { MicrosoftTodoIntegrationQuery } from "~/__generated__/MicrosoftTodoIntegrationQuery.graphql";

import styles from "./MicrosoftTodo.module.css";

export type MicrosoftTodoResponseType = NonNullable<
	MicrosoftTodoIntegrationListsQuery["response"]["viewer"]["integrations"]["microsoftTodo"]
>;
export type MicrosoftTodoListsListType = NonNullable<MicrosoftTodoResponseType["lists"]>;
export type MicrosoftTodoListType = NonNullable<MicrosoftTodoResponseType["lists"]>[0];

export const microsoftTodoKey = "MICROSOFT_TODO";

graphql`
	fragment MicrosoftTodoIntegrationUserIntegrationDetails on MicrosoftTodoUserIntegrationNode {
		microsoftTodoUser {
			user {
				fullName
			}
		}
		externalListName
		externalListId
	}
`;

export const CreateMicrosoftTodoUser = graphql`
	mutation MicrosoftTodoIntegrationCreateUserMutation($input: CreateMicrosoftTodoUserInput!) {
		createMicrosoftTodoUser(input: $input) {
			viewer {
				integrations {
					microsoftTodo {
						id
						isConnected
						unsyncedActionItemCount
					}
				}
			}
		}
	}
`;

export const MicrosoftTodoListsQuery = graphql`
	query MicrosoftTodoIntegrationListsQuery {
		viewer {
			integrations {
				microsoftTodo {
					lists {
						name
						externalId
					}
				}
			}
		}
	}
`;

type MicrosoftTodoSettingsProps = {
	onBackClick?: () => void;
};

export type AddOrChangeListResponseProps = {
	listId: string;
	listName: string;
	syncEnabled: boolean;
	triggerManualSync: boolean;
};

export const connectMicrosoftTodo = (params: OAuthParams = {}): Promise<AuthPostMessage> => {
	return new Promise((resolve, reject) => {
		promptOffice365Auth({ msTodo: true, ...params })
			.then(data => {
				if (data.result === "ok") {
					commitMutation<MicrosoftTodoIntegrationCreateUserMutation>(environment, {
						mutation: CreateMicrosoftTodoUser,
						variables: {
							input: {},
						},
						onCompleted: () => {
							toast.success("Connected to Microsoft To Do");
							resolve(data);
						},
					});
				} else {
					reject(new Error("Failed to connect to Microsoft To Do"));
				}
			})
			.catch(error => {
				toast.error("Failed to connect to Microsoft To Do");
				Sentry.captureException(error);
			});
	});
};

function MicrosoftTodoIntegration(props: MicrosoftTodoSettingsProps) {
	const { viewer } = useLazyLoadQuery<MicrosoftTodoIntegrationQuery>(
		graphql`
			query MicrosoftTodoIntegrationQuery {
				viewer {
					account {
						billingAccount {
							hasAccessToMicrosoftTodoIntegration: hasAccessToFeature(feature: microsoft_todo_integration)
						}
					}
					integrations {
						microsoftTodo {
							id
							isConnected
							unsyncedActionItemCount
						}
						asana {
							connectedProject {
								id
							}
						}
						userLevelActionItemIntegration {
							id
							integrationProvider
							syncEnabled
							syncedActionItemCount
							unsyncedActionItemCount
							lastSyncDate
							integrationSpecificDetails {
								... on MicrosoftTodoUserIntegrationNode {
									id
									externalListName
									externalListId
								}
							}
						}
					}
				}
			}
		`,
		{},
		{},
	);

	const microsoftTodoNode = viewer;

	const [tab, setTab] = useState("settings");
	const [syncPending, setSyncPending] = useState(false);

	// NOTE: I'm pretty sure none of the following needs to be state, but I'm not refactoring it right now -C
	const [syncedList, setSyncedList] = useState<MicrosoftTodoListType | undefined>();
	const [syncEnabled, setSyncEnabled] = useState(false);
	const [syncedCount, setSyncedCount] = useState(0);
	const [unsyncedCount, setUnsyncedCount] = useState<number>(0);
	const [initialUnsyncedCount, setInitialUnsyncedCount] = useState<number>(0);
	const [lastSyncDate, setLastSyncDate] = useState<string | undefined>();
	const [connectionId, setConnectionId] = useState<string | undefined>();
	const [todoLists, setTodoLists] = useState<MicrosoftTodoListsListType>();

	useEffect(() => {
		if (microsoftTodoNode.integrations.microsoftTodo?.isConnected) {
			loadLists();
		}
	}, [microsoftTodoNode.integrations.microsoftTodo?.isConnected]);

	useEffect(() => {
		if (microsoftTodoNode.integrations.userLevelActionItemIntegration) {
			const list = {
				externalId:
					microsoftTodoNode.integrations.userLevelActionItemIntegration.integrationSpecificDetails.externalListId ?? "",
				name:
					microsoftTodoNode.integrations.userLevelActionItemIntegration.integrationSpecificDetails.externalListName ??
					"",
			};
			setSyncedList(list);
			setSyncEnabled(microsoftTodoNode.integrations.userLevelActionItemIntegration.syncEnabled ? true : false);
			setSyncedCount(microsoftTodoNode.integrations.userLevelActionItemIntegration.syncedActionItemCount ?? 0);
			setUnsyncedCount(microsoftTodoNode.integrations.userLevelActionItemIntegration.unsyncedActionItemCount ?? 0);
			setLastSyncDate(microsoftTodoNode.integrations.userLevelActionItemIntegration.lastSyncDate ?? undefined);
		} else {
			setSyncedList(undefined);
			setSyncEnabled(false);
			setSyncedCount(0);
			setLastSyncDate(undefined);
		}
		if (microsoftTodoNode.integrations.microsoftTodo) {
			setConnectionId(microsoftTodoNode.integrations.microsoftTodo.id);
			setInitialUnsyncedCount(microsoftTodoNode.integrations.microsoftTodo.unsyncedActionItemCount ?? 0);
		}
	}, [microsoftTodoNode]);

	const loadLists = () => {
		fetchQuery<MicrosoftTodoIntegrationListsQuery>(environment, MicrosoftTodoListsQuery, {})
			.toPromise()
			.then(response => {
				const lists = response?.viewer.integrations.microsoftTodo?.lists || [];
				setTodoLists(lists);
			});
	};

	const getTabs = () => {
		if (microsoftTodoNode.integrations.microsoftTodo?.isConnected) {
			return [
				{
					label: "Settings",
					key: "settings",
				},
				{
					label: "Getting started",
					key: "about",
				},
			];
		}

		return [];
	};

	const onTabClick = useCallback((tab: string) => {
		setTab(tab);
	}, []);

	const onSyncActionItemsClick = useCallback(async () => {
		setSyncPending(true);
		commitMutation<ActionItemIntegrationsSyncIncompleteActionItemsMutation>(environment, {
			mutation: syncIncompleteActionItems,
			variables: {},
			onCompleted: () => {
				setSyncPending(false);
			},
		});
	}, []);

	const toggleSync = useCallback(
		(e: React.FormEvent, data: CheckboxProps): void => {
			if (!microsoftTodoNode.integrations.userLevelActionItemIntegration) {
				return;
			}
			commitMutation<ActionItemIntegrationsToggleSyncMutation>(environment, {
				mutation: toggleUserIntegrationSync,
				variables: {
					syncEnabled: data.checked ? true : false,
				},
			});
		},
		[microsoftTodoNode],
	);

	const onDisconnectClicked = useCallback(async () => {
		const result = await __globalModalContext?.open<boolean>(({ close }) => (
			<MicrosoftTodoDisconnectModal onClose={close} connectionId={connectionId || ""} />
		));
		if (!result) toast.error(`Failed to disconnect Microsoft To Do`);
	}, [connectionId]);

	const onRemoveList = useCallback((): void => {
		commitMutation<ActionItemIntegrationsDisconnectUserMutation>(environment, {
			mutation: disconnectUserFromIntegration,
			variables: {
				input: {},
			},
			onCompleted: deletedUserIntegrationId => {
				if (deletedUserIntegrationId) {
					toast.success(`Removed list`);
				} else {
					toast.error(`Failed to remove list`);
				}
			},
		});
	}, []);

	const onAddList = useCallback((data: AddOrChangeListResponseProps): void => {
		commitMutation<ActionItemIntegrationsConnectUserMutation>(environment, {
			mutation: connectUserToIntegration,
			variables: {
				integrationProvider: microsoftTodoKey,
				destinationInfo: {
					/* eslint-disable camelcase */
					list_id: data.listId,
					list_name: data.listName,
					/* eslint-enable camelcase */
				},
				syncEnabled: data.syncEnabled,
				triggerManualSync: data.triggerManualSync,
			},
			onCompleted: result => {
				if (result) {
					if (result.connectUserToIntegration) {
						toast.success(`Added list`);
					} else {
						toast.error(`Failed to add list - Asana project is connected`);
					}
				} else {
					toast.error(`Failed to add list`);
				}
			},
		});
	}, []);

	const onSelectList = useCallback(async () => {
		const result = await __globalModalContext?.open<AddOrChangeListResponseProps | undefined>(({ close }) => (
			<MicrosoftTodoAddListModal
				onClose={close}
				todoLists={todoLists || []}
				actionItemCount={initialUnsyncedCount}
				defaultList={syncedList ? { value: syncedList.externalId, label: syncedList.name } : null}
			/>
		));
		if (!result) {
			return;
		}
		if (syncedList) {
			result.syncEnabled = syncEnabled;
		}
		onAddList(result);
	}, [syncedList, onAddList, todoLists, initialUnsyncedCount, syncEnabled]);

	const renderInfoPage = () => {
		return <AboutMicrosoftTodo />;
	};

	const renderListSettingsPage = () => {
		return (
			<MicrosoftTodoListSettings
				list={syncedList}
				syncEnabled={syncEnabled}
				syncPending={syncPending}
				onChangeList={onSelectList}
				onRemoveList={onRemoveList}
				onToggleSync={toggleSync}
				onSyncActionItems={onSyncActionItemsClick}
				syncedCount={syncedCount}
				unsyncedCount={unsyncedCount}
				lastSyncDate={lastSyncDate}
			/>
		);
	};

	const renderSelectListPage = useCallback(() => {
		const listSelectionDisabled = microsoftTodoNode.integrations.asana?.connectedProject ? true : false;

		return (
			<div className={styles.selectListPage}>
				<MicrosoftTodoLogo className={styles.logo} />
				<h4>Select a Microsoft To Do list</h4>
				<Button
					size="wide"
					variant="primary"
					onClick={onSelectList}
					disabled={todoLists === undefined || listSelectionDisabled}
				>
					Select a list {todoLists === undefined && !listSelectionDisabled && <LoadingSpinner inline dimmer={false} />}
				</Button>
				{listSelectionDisabled && (
					<p className={styles.selectListDisabledText}>
						Note: You can only have one user level action item integration connected at a given time.
						<br />
						<Link to={"/settings/integrations/asana"}>
							<span>Remove your Asana Project to continue.</span>
						</Link>
					</p>
				)}
			</div>
		);
	}, [microsoftTodoNode.integrations.asana?.connectedProject, onSelectList, todoLists]);

	const paywalled = !microsoftTodoNode.account?.billingAccount.hasAccessToMicrosoftTodoIntegration;
	const connected = microsoftTodoNode.integrations.microsoftTodo?.isConnected ?? false;
	const connectedList = microsoftTodoNode.integrations.userLevelActionItemIntegration?.id ? true : false;

	return (
		<React.Fragment>
			<Settings
				name="Microsoft To Do"
				connected={connected}
				logo={<MicrosoftTodoLogo style={{ paddingBottom: "10px" }} />}
				paywalledButton={undefined}
				paywalledBanner={paywalled ? "fellow_pro_ai" : undefined}
				paywallFeature="microsoft_todo_integration"
				installText="Connect"
				tabs={getTabs()}
				activeTab={tab}
				onTabClick={onTabClick}
				onInstall={connectMicrosoftTodo}
				onDisconnect={onDisconnectClicked}
				backPath="/settings/integrations/"
				onBackClick={props.onBackClick}
				installIcon={faBolt}
			>
				{!connected || tab === "about"
					? renderInfoPage()
					: connectedList
						? renderListSettingsPage()
						: renderSelectListPage()}
			</Settings>
		</React.Fragment>
	);
}

export default MicrosoftTodoIntegration;
